import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout.jsx';

const h2Style = {
  fontSize: 19,
  fontWeight: 600,
  marginTop: 28,
  marginBottom: 10,
};
const pStyle = { marginBottom: 14 };
const italicLabelStyle = {
  fontStyle: 'italic',
  marginTop: 16,
  marginBottom: 8,
};
const ulStyle = { marginBottom: 14, paddingLeft: 24 };
const liStyle = { marginBottom: 6 };

export default function Privacy() {
  return (
    <LegalPageLayout title="Privacy Policy — Rooted" lastUpdated="May 13, 2026">
      <h2 style={h2Style}>1. Introduction</h2>
      <p style={pStyle}>
        Golden Fox Publishing LLC (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;)
        operates Rooted (the &ldquo;Service&rdquo;). This Privacy Policy describes how we
        collect, use, store, and share information about you when you use the
        Service. By using the Service, you agree to the practices described in
        this Policy.
      </p>

      <h2 style={h2Style}>2. Information We Collect</h2>
      <p style={italicLabelStyle}>Information You Provide Directly:</p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          <strong>Account information:</strong> name, email address, and
          authentication credentials (managed via Clerk)
        </li>
        <li style={liStyle}>
          <strong>Payment information:</strong> processed directly by Stripe; we
          do not store full credit card numbers
        </li>
        <li style={liStyle}>
          <strong>Intake conversation content:</strong> text and voice responses
          provided during onboarding
        </li>
        <li style={liStyle}>
          <strong>Voice recordings:</strong> audio of your spoken responses,
          transcribed via OpenAI
        </li>
        <li style={liStyle}>
          <strong>Assessment responses:</strong> your answers to the Enneagram
          and other personality assessments
        </li>
        <li style={liStyle}>
          <strong>Reflection content:</strong> written or spoken responses
          during daily check-ins
        </li>
        <li style={liStyle}>
          <strong>Support communications:</strong> messages you send to us via
          email or other channels
        </li>
      </ul>
      <p style={italicLabelStyle}>Information Collected Automatically:</p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          <strong>Usage data:</strong> features accessed, time spent,
          interactions with the Service
        </li>
        <li style={liStyle}>
          <strong>Device information:</strong> browser type, operating system,
          IP address, device identifiers
        </li>
        <li style={liStyle}>
          <strong>Cookies and similar technologies:</strong> session cookies for
          authentication; error-monitoring beacons via Sentry
        </li>
      </ul>

      <h2 style={h2Style}>3. How We Use Information</h2>
      <p style={pStyle}>We use your information to:</p>
      <ul style={ulStyle}>
        <li style={liStyle}>Provide, operate, and maintain the Service</li>
        <li style={liStyle}>
          Generate personalized study content based on your inputs and
          assessment results
        </li>
        <li style={liStyle}>
          Process subscription payments and manage your account
        </li>
        <li style={liStyle}>
          Communicate with you about the Service, including support, updates,
          and notices
        </li>
        <li style={liStyle}>
          Improve and develop the Service, including through aggregated and
          anonymized analysis
        </li>
        <li style={liStyle}>
          Detect, prevent, and address technical issues, fraud, or abuse
        </li>
        <li style={liStyle}>Comply with legal obligations</li>
      </ul>

      <h2 style={h2Style}>4. Third-Party Service Providers</h2>
      <p style={pStyle}>
        We use the following third-party services to operate the Service. Each
        may receive specific information necessary to perform its function:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          <strong>Anthropic</strong> (anthropic.com) — provides AI models that
          generate study content. Your conversation content and assessment data
          is sent to Anthropic for processing.
        </li>
        <li style={liStyle}>
          <strong>OpenAI</strong> (openai.com) — transcribes your voice
          recordings into text. Audio files are sent to OpenAI&apos;s speech
          recognition service.
        </li>
        <li style={liStyle}>
          <strong>ElevenLabs</strong> (elevenlabs.io) — generates synthesized
          voice audio. Text is sent to ElevenLabs for audio synthesis.
        </li>
        <li style={liStyle}>
          <strong>Clerk</strong> (clerk.com) — manages user authentication and
          account credentials.
        </li>
        <li style={liStyle}>
          <strong>Stripe</strong> (stripe.com) — processes subscription
          payments. Stripe receives payment method details and billing
          information directly.
        </li>
        <li style={liStyle}>
          <strong>Supabase</strong> (supabase.com) — provides database storage
          for your account data, conversation transcripts, and study content.
        </li>
        <li style={liStyle}>
          <strong>Vercel</strong> (vercel.com) — hosts the Service
          infrastructure.
        </li>
        <li style={liStyle}>
          <strong>Sentry</strong> (sentry.io) — collects error reports and
          limited diagnostic data to help us identify and fix issues.
        </li>
      </ul>
      <p style={pStyle}>
        Each provider has its own privacy practices. We encourage you to review
        their respective policies.
      </p>

      <h2 style={h2Style}>5. Data Sharing</h2>
      <p style={pStyle}>
        We do not sell your personal information. We may share information:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          With the third-party providers listed in Section 4, as necessary to
          operate the Service
        </li>
        <li style={liStyle}>
          To comply with legal obligations, court orders, or government requests
        </li>
        <li style={liStyle}>
          To protect the rights, property, or safety of Golden Fox Publishing
          LLC, our users, or others
        </li>
        <li style={liStyle}>
          In connection with a business transaction such as a merger,
          acquisition, or sale of assets (with notice to you)
        </li>
        <li style={liStyle}>With your explicit consent</li>
      </ul>

      <h2 style={h2Style}>6. Data Retention</h2>
      <p style={pStyle}>
        We retain your information for as long as your account is active and as
        needed to provide the Service. Upon cancellation, we retain certain
        information for up to twelve (12) months for legal, accounting, and
        abuse-prevention purposes. You may request earlier deletion (see Section
        8). Aggregated and anonymized data may be retained indefinitely.
      </p>

      <h2 style={h2Style}>7. Security</h2>
      <p style={pStyle}>
        We implement reasonable technical and organizational measures designed
        to protect your information, including:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}>Encryption in transit (HTTPS/TLS)</li>
        <li style={liStyle}>
          Encryption at rest where supported by our infrastructure providers
        </li>
        <li style={liStyle}>Access controls limiting who can view your data</li>
        <li style={liStyle}>
          Regular review of our security practices and providers
        </li>
      </ul>
      <p style={pStyle}>
        However, no method of transmission over the internet or electronic
        storage is one hundred percent secure. We cannot guarantee absolute
        security.
      </p>

      <h2 style={h2Style}>8. Your Rights</h2>
      <p style={pStyle}>
        Depending on your jurisdiction, you may have the following rights
        regarding your information:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          <strong>Access:</strong> request a copy of the information we hold
          about you
        </li>
        <li style={liStyle}>
          <strong>Correction:</strong> request correction of inaccurate
          information
        </li>
        <li style={liStyle}>
          <strong>Deletion:</strong> request that we delete your information,
          subject to certain exceptions (legal obligations, abuse prevention)
        </li>
        <li style={liStyle}>
          <strong>Portability:</strong> request your information in a portable
          format
        </li>
        <li style={liStyle}>
          <strong>Objection and restriction:</strong> object to or restrict
          certain processing
        </li>
      </ul>
      <p style={pStyle}>
        To exercise these rights, email{' '}
        <a href="mailto:mike@goldenfoxrooted.com">mike@goldenfoxrooted.com</a>.
        We will respond within a reasonable timeframe and in accordance with
        applicable law.
      </p>

      <h2 style={h2Style}>9. Children&apos;s Privacy</h2>
      <p style={pStyle}>
        The Service is not intended for use by individuals under eighteen (18)
        years of age. We do not knowingly collect information from anyone under
        eighteen. If you become aware that a minor has provided us information,
        contact us immediately and we will delete such information.
      </p>

      <h2 style={h2Style}>10. International Users</h2>
      <p style={pStyle}>
        The Service is operated from the United States. If you access the
        Service from outside the United States, your information will be
        transferred to, stored in, and processed in the United States and other
        jurisdictions where our service providers operate. By using the Service,
        you consent to such transfers.
      </p>

      <h2 style={h2Style}>11. California Privacy Rights (CCPA/CPRA)</h2>
      <p style={pStyle}>
        If you are a California resident, you have rights under the California
        Consumer Privacy Act and California Privacy Rights Act, including the
        right to know what information we collect, sell, or disclose; the right
        to delete; the right to correct; and the right to non-discrimination for
        exercising these rights. We do not sell personal information. To
        exercise these rights, email{' '}
        <a href="mailto:mike@goldenfoxrooted.com">mike@goldenfoxrooted.com</a>.
      </p>

      <h2 style={h2Style}>12. European Users (GDPR)</h2>
      <p style={pStyle}>
        If you are in the European Economic Area, you have rights under the
        General Data Protection Regulation, including access, rectification,
        erasure, restriction of processing, portability, and objection. The
        legal basis for our processing is contract performance (providing the
        Service you subscribed to) and our legitimate interests (improving the
        Service, fraud prevention). You may lodge a complaint with your local
        data protection authority. To exercise GDPR rights, email{' '}
        <a href="mailto:mike@goldenfoxrooted.com">mike@goldenfoxrooted.com</a>.
      </p>

      <h2 style={h2Style}>13. Cookies and Tracking</h2>
      <p style={pStyle}>The Service uses cookies and similar technologies for:</p>
      <ul style={ulStyle}>
        <li style={liStyle}>
          <strong>Authentication</strong> (essential — required for the Service
          to function)
        </li>
        <li style={liStyle}>
          <strong>Error monitoring</strong> (Sentry — limited diagnostic data)
        </li>
      </ul>
      <p style={pStyle}>
        We do not use cookies for behavioral advertising. You may control
        cookies through your browser settings; disabling essential cookies will
        prevent the Service from functioning.
      </p>

      <h2 style={h2Style}>14. Changes to This Policy</h2>
      <p style={pStyle}>
        We may update this Privacy Policy from time to time. We will provide
        notice of material changes by email or through the Service. The &ldquo;Last
        updated&rdquo; date at the top of this Policy reflects the latest revision.
      </p>

      <h2 style={h2Style}>15. Contact Us</h2>
      <p style={pStyle}>
        Questions about this Privacy Policy or our data practices? Contact us
        at:
      </p>
      <p style={pStyle}>
        Golden Fox Publishing LLC
        <br />
        <a href="mailto:mike@goldenfoxrooted.com">mike@goldenfoxrooted.com</a>
        <br />
        Fishers, Indiana, USA
      </p>
    </LegalPageLayout>
  );
}
